"use client";

import { c as _c } from "react/compiler-runtime";
import { ListItemGroup } from "@/components/list/ListItemButton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/Collapsible";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/DropdownMenu";
import { IconButton } from "@/components/ui/IconButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { Spinner } from "@/components/ui/Spinner";
import { Text } from "@/components/ui/Text";
import { cva } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import { ArrowDown, ArrowUp, Check, ChevronRight, CircleAlert, Copy, MoreHorizontal, Paperclip, Square, SquarePen, Wrench, X } from "lucide-react";
import type { ReactNode } from "react";
import * as React from "react";
import cn from "twsx";

// The agent chat surface (Atlas assistant and Diffuse), measured from Linear's
// agent chat: a 256px conversation list beside a centred 768px transcript,
// 13/24 body text, user turns in a borderless right-aligned bubble, agent turns
// unboxed, and one bordered composer docked under the transcript. Below md the
// list and the conversation are separate screens. Nothing animates except the
// Spinner and Skeleton.

const messageText = "min-w-0 text-base leading-6 text-text-primary";

/** A message the user sent: right-aligned bubble, with its attachments above and hover actions beside it. */
export function ChatUserMessage(t0) {
  const $ = _c(12);
  const {
    children,
    attachments,
    actions,
    label
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="sr-only">You:</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== attachments) {
    t2 = attachments && <div className="flex flex-wrap justify-end gap-1.5">{attachments}</div>;
    $[1] = attachments;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== actions || $[4] !== children) {
    t3 = children && <div className="flex min-w-0 max-w-full items-start gap-1.5">{actions && <span className="flex h-10 shrink-0 items-center">{actions}</span>}<div className={cn(messageText, "whitespace-pre-wrap break-words rounded-control bg-fill-bubble px-3 py-2")}>{children}</div></div>;
    $[3] = actions;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex w-3/4 flex-col items-end gap-1.5">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== label || $[10] !== t4) {
    t5 = <article aria-label={label} className="group flex flex-col items-end">{t1}{t4}</article>;
    $[9] = label;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/** The agent's reply: its activity and the answer, unboxed, with hover actions under it. */
export function ChatAgentMessage(t0) {
  const $ = _c(8);
  const {
    children,
    actions,
    label
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(messageText, "group flex w-full flex-col gap-3");
    t2 = <span className="sr-only">Agent:</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== actions) {
    t3 = actions && <div className="-mt-1 flex h-6 items-center gap-0.5">{actions}</div>;
    $[2] = actions;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== label || $[6] !== t3) {
    t4 = <article aria-label={label} className={t1}>{t2}{children}{t3}</article>;
    $[4] = children;
    $[5] = label;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}

/** When the conversation started, centred above its first turn. */
export function ChatTimestamp(t0) {
  const $ = _c(7);
  const {
    date
  } = t0;
  let day;
  let t1;
  let t2;
  if ($[0] !== date) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const value = new Date(date);
      if (Number.isNaN(value.getTime())) {
        t2 = null;
        break bb0;
      }
      day = value.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: value.getFullYear() === new Date().getFullYear() ? undefined : "numeric"
      });
      t1 = value.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit"
      });
    }
    $[0] = date;
    $[1] = day;
    $[2] = t1;
    $[3] = t2;
  } else {
    day = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const time = t1;
  const t3 = `${day} at ${time}`;
  let t4;
  if ($[4] !== date || $[5] !== t3) {
    t4 = <time dateTime={date} className="self-center text-sm font-medium text-text-quaternary">{t3}</time>;
    $[4] = date;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

/** The agent is working: a spinner and what it is doing now. */
export function ChatThinking(t0) {
  const $ = _c(3);
  const {
    label: t1
  } = t0;
  const label = t1 === undefined ? "Thinking" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Spinner size="xs" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== label) {
    t3 = <div role="status" aria-live="polite" className="flex min-w-0 items-center gap-2 text-base font-medium leading-5 text-text-tertiary">{t2}<span className="truncate">{label}</span></div>;
    $[1] = label;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}

/** Small print under a turn: no response, stopped, scope notes. */
export function ChatNote(t0) {
  const $ = _c(7);
  const {
    tone: t1,
    icon,
    children
  } = t0;
  const tone = t1 === undefined ? "muted" : t1;
  const t2 = tone === "danger" ? "alert" : undefined;
  const t3 = tone === "danger" ? "text-red-text" : "text-text-tertiary";
  let t4;
  if ($[0] !== t3) {
    t4 = cn("flex items-center gap-1.5 text-base leading-5 [&_svg]:size-3.5 [&_svg]:shrink-0", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== children || $[3] !== icon || $[4] !== t2 || $[5] !== t4) {
    t5 = <p role={t2} className={t4}>{icon}{children}</p>;
    $[2] = children;
    $[3] = icon;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
const attachmentIconVariants = cva("grid size-8 shrink-0 place-items-center rounded-control [&_svg]:size-4", {
  variants: {
    tone: {
      file: "bg-bg-secondary text-text-secondary",
      sheet: "bg-green-bg-subtle text-green-text",
      pdf: "bg-red-bg-subtle text-red-text",
      doc: "bg-blue-bg-subtle text-blue-text",
      image: "bg-navy-50 text-navy-700",
      mail: "bg-bg-secondary text-text-secondary"
    }
  },
  defaultVariants: {
    tone: "file"
  }
});
export type ChatAttachmentTone = "file" | "sheet" | "pdf" | "doc" | "image" | "mail";

/** A file attached to a message or waiting in the composer. With `href` it is a link that opens or downloads the file. */
export function ChatAttachment(t0) {
  const $ = _c(40);
  const {
    name,
    meta,
    icon,
    tone,
    loading: t1,
    error,
    onRemove,
    disabled,
    href,
    download,
    label
  } = t0;
  const loading = t1 === undefined ? false : t1;
  let content;
  let frame;
  let t2;
  if ($[0] !== download || $[1] !== error || $[2] !== href || $[3] !== icon || $[4] !== label || $[5] !== loading || $[6] !== meta || $[7] !== name || $[8] !== tone) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      frame = cn("inline-flex h-12 min-w-0 max-w-80 items-center gap-2.5 rounded-control border bg-bg-surface px-2", error ? "border-red-bg-subtle" : "border-border-primary");
      let t3;
      if ($[12] !== tone) {
        t3 = attachmentIconVariants({
          tone
        });
        $[12] = tone;
        $[13] = t3;
      } else {
        t3 = $[13];
      }
      let t4;
      if ($[14] !== error || $[15] !== icon || $[16] !== loading) {
        t4 = loading ? <Spinner size="sm" /> : error ? <CircleAlert /> : icon;
        $[14] = error;
        $[15] = icon;
        $[16] = loading;
        $[17] = t4;
      } else {
        t4 = $[17];
      }
      let t5;
      if ($[18] !== t3 || $[19] !== t4) {
        t5 = <span aria-hidden="true" className={t3}>{t4}</span>;
        $[18] = t3;
        $[19] = t4;
        $[20] = t5;
      } else {
        t5 = $[20];
      }
      let t6;
      if ($[21] !== name) {
        t6 = <span className="truncate text-base font-medium text-text-primary">{name}</span>;
        $[21] = name;
        $[22] = t6;
      } else {
        t6 = $[22];
      }
      let t7;
      if ($[23] !== error || $[24] !== meta) {
        t7 = (error ?? meta) && <span className={cn("truncate text-sm", error ? "text-red-text" : "text-text-tertiary")}>{error ?? meta}</span>;
        $[23] = error;
        $[24] = meta;
        $[25] = t7;
      } else {
        t7 = $[25];
      }
      let t8;
      if ($[26] !== t6 || $[27] !== t7) {
        t8 = <span className="flex min-w-0 flex-col leading-tight">{t6}{t7}</span>;
        $[26] = t6;
        $[27] = t7;
        $[28] = t8;
      } else {
        t8 = $[28];
      }
      let t9;
      if ($[29] !== t5 || $[30] !== t8) {
        t9 = <>{t5}{t8}</>;
        $[29] = t5;
        $[30] = t8;
        $[31] = t9;
      } else {
        t9 = $[31];
      }
      content = t9;
      if (href) {
        t2 = <a href={href} aria-label={label} download={download} target={download ? undefined : "_blank"} rel={download ? undefined : "noreferrer"} className={cn(frame, "pr-3 outline-none hover:bg-fill-hover-subtle focus-visible:outline-1 focus-visible:outline-accent")}>{content}</a>;
        break bb0;
      }
    }
    $[0] = download;
    $[1] = error;
    $[2] = href;
    $[3] = icon;
    $[4] = label;
    $[5] = loading;
    $[6] = meta;
    $[7] = name;
    $[8] = tone;
    $[9] = content;
    $[10] = frame;
    $[11] = t2;
  } else {
    content = $[9];
    frame = $[10];
    t2 = $[11];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[32] !== disabled || $[33] !== name || $[34] !== onRemove) {
    t3 = onRemove && <IconButton label={`Remove ${name}`} size={24} disabled={disabled} onClick={onRemove}><X aria-hidden="true" /></IconButton>;
    $[32] = disabled;
    $[33] = name;
    $[34] = onRemove;
    $[35] = t3;
  } else {
    t3 = $[35];
  }
  let t4;
  if ($[36] !== content || $[37] !== frame || $[38] !== t3) {
    t4 = <div className={frame}>{content}{t3}</div>;
    $[36] = content;
    $[37] = frame;
    $[38] = t3;
    $[39] = t4;
  } else {
    t4 = $[39];
  }
  return t4;
}

/**
 * Linear's comment composer: one bordered surface with the field on top and
 * attach plus send (or stop) on the right below it. Send is disabled until
 * there is something to send.
 */
export function ChatComposer(t0) {
  const $ = _c(39);
  let attachments;
  let canSend;
  let children;
  let onAttach;
  let onStop;
  let onSubmit;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      children,
      attachments,
      onSubmit,
      onAttach,
      attachDisabled: t1,
      canSend,
      running: t2,
      onStop,
      stopping: t3,
      dragging: t4,
      label: t5,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = attachments;
    $[2] = canSend;
    $[3] = children;
    $[4] = onAttach;
    $[5] = onStop;
    $[6] = onSubmit;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    attachments = $[1];
    canSend = $[2];
    children = $[3];
    onAttach = $[4];
    onStop = $[5];
    onSubmit = $[6];
    props = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
  }
  const attachDisabled = t1 === undefined ? false : t1;
  const running = t2 === undefined ? false : t2;
  const stopping = t3 === undefined ? false : t3;
  const dragging = t4 === undefined ? false : t4;
  const label = t5 === undefined ? "Message composer" : t5;
  const t6 = dragging ? "border-accent" : "border-border-input focus-within:border-border-input-hover";
  let t7;
  if ($[13] !== t6) {
    t7 = cn("flex w-full min-w-0 cursor-text flex-col gap-2 rounded-settings-list-view-item border bg-bg-surface p-3 shadow-low", t6);
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== canSend || $[16] !== onSubmit) {
    t8 = event => {
      event.preventDefault();
      if (canSend) {
        onSubmit();
      }
    };
    $[15] = canSend;
    $[16] = onSubmit;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== attachments) {
    t9 = attachments && <div className="flex flex-wrap gap-2">{attachments}</div>;
    $[18] = attachments;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== attachDisabled || $[21] !== onAttach) {
    t10 = onAttach && <IconButton label="Add attachments" size={24} disabled={attachDisabled} onClick={event_0 => {
      event_0.stopPropagation();
      onAttach();
    }}><Paperclip aria-hidden="true" /></IconButton>;
    $[20] = attachDisabled;
    $[21] = onAttach;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== canSend || $[24] !== onStop || $[25] !== running || $[26] !== stopping) {
    t11 = running ? <IconButton label="Stop response" size={24} variant="neutral" disabled={stopping} onClick={event_1 => {
      event_1.stopPropagation();
      onStop?.();
    }}><Square className="fill-current" aria-hidden="true" /></IconButton> : <IconButton label="Send message" size={24} type="submit" variant="accent" disabled={!canSend} onClick={_temp}><ArrowUp aria-hidden="true" /></IconButton>;
    $[23] = canSend;
    $[24] = onStop;
    $[25] = running;
    $[26] = stopping;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11) {
    t12 = <div className="flex items-center justify-end gap-2">{t10}{t11}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== children || $[32] !== label || $[33] !== props || $[34] !== t12 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t13 = <form aria-label={label} className={t7} onSubmit={t8} {...props}>{t9}{children}{t12}</form>;
    $[31] = children;
    $[32] = label;
    $[33] = props;
    $[34] = t12;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
}
function _temp(event_2) {
  return event_2.stopPropagation();
}
const composerField = cn("block max-h-72 min-h-6 w-full overflow-y-auto border-0 bg-transparent px-0.5", "text-base leading-6 text-text-primary outline-none", "placeholder:text-text-placeholder disabled:opacity-disabled aria-disabled:opacity-disabled");
function sendOnEnter(event: React.KeyboardEvent, onSend: () => void) {
  if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
    event.preventDefault();
    onSend();
  }
}

/** The composer's text field: one line that grows to 288px; Enter sends and Shift+Enter adds a line. */
export function ChatComposerInput(t0) {
  const $ = _c(13);
  let onKeyDown;
  let onSend;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      onSend,
      onKeyDown,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onKeyDown;
    $[2] = onSend;
    $[3] = props;
    $[4] = ref;
  } else {
    onKeyDown = $[1];
    onSend = $[2];
    props = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(composerField, "resize-none [field-sizing:content]");
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== onKeyDown || $[7] !== onSend) {
    t2 = event => {
      onKeyDown?.(event);
      sendOnEnter(event, onSend);
    };
    $[6] = onKeyDown;
    $[7] = onSend;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== props || $[10] !== ref || $[11] !== t2) {
    t3 = <textarea ref={ref} rows={1} className={t1} onKeyDown={t2} {...props} />;
    $[9] = props;
    $[10] = ref;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}

/** The same field as a rich-text editable, for composers that read pasted content from the DOM. */
export function ChatComposerEditable(t0) {
  const $ = _c(18);
  let onKeyDown;
  let onSend;
  let placeholder;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      onSend,
      onKeyDown,
      placeholder,
      disabled: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onKeyDown;
    $[2] = onSend;
    $[3] = placeholder;
    $[4] = props;
    $[5] = ref;
    $[6] = t1;
  } else {
    onKeyDown = $[1];
    onSend = $[2];
    placeholder = $[3];
    props = $[4];
    ref = $[5];
    t1 = $[6];
  }
  const disabled = t1 === undefined ? false : t1;
  const t2 = !disabled;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(composerField, "empty:before:pointer-events-none empty:before:text-text-placeholder empty:before:content-placeholder");
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== onKeyDown || $[9] !== onSend) {
    t4 = event => {
      onKeyDown?.(event);
      sendOnEnter(event, onSend);
    };
    $[8] = onKeyDown;
    $[9] = onSend;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== disabled || $[12] !== placeholder || $[13] !== props || $[14] !== ref || $[15] !== t2 || $[16] !== t4) {
    t5 = <div ref={ref} role="textbox" aria-multiline="true" aria-disabled={disabled} contentEditable={t2} suppressContentEditableWarning={true} data-placeholder={placeholder} className={t3} onKeyDown={t4} {...props} />;
    $[11] = disabled;
    $[12] = placeholder;
    $[13] = props;
    $[14] = ref;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}

/** The agent's working steps for one stretch of a turn, folded behind a one-line summary ("Worked for 10s"). */
/** Use when building the assistant or Diffuse chat screens. */
export function ChatActivity(t0) {
  const $ = _c(38);
  const {
    title,
    meta,
    active: t1,
    tone: t2,
    defaultOpen: t3,
    detail,
    children
  } = t0;
  const active = t1 === undefined ? false : t1;
  const tone = t2 === undefined ? "default" : t2;
  const defaultOpen = t3 === undefined ? false : t3;
  const [toggled, setToggled] = React.useState(null);
  const open = toggled ?? (defaultOpen || active && !detail);
  let t4;
  if ($[0] !== active) {
    t4 = active && <Spinner size="xs" />;
    $[0] = active;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== title) {
    t5 = <span className="min-w-0 truncate">{title}</span>;
    $[2] = title;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== meta) {
    t6 = meta && <span className="shrink-0 tabular-nums text-text-quaternary">{meta}</span>;
    $[4] = meta;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t4 || $[7] !== t5 || $[8] !== t6) {
    t7 = <>{t4}{t5}{t6}</>;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const summary = t7;
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t8;
  let t9;
  if ($[10] !== children || $[11] !== detail || $[12] !== open || $[13] !== summary || $[14] !== tone) {
    t12 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const lineClassName = cn("-mx-1 inline-flex max-w-full items-center gap-1.5 rounded-kbd px-1 text-base font-medium leading-6", tone === "danger" ? "text-red-text" : "text-text-tertiary");
      if (!children && !detail) {
        t12 = <div className={lineClassName}>{summary}</div>;
        break bb0;
      }
      T1 = Collapsible;
      t9 = open;
      t10 = setToggled;
      t11 = "min-w-0";
      T0 = CollapsibleTrigger;
      t8 = cn(lineClassName, "group outline-none hover:bg-fill-hover-subtle hover:text-text-secondary", "focus-visible:outline-1 focus-visible:outline-accent");
    }
    $[10] = children;
    $[11] = detail;
    $[12] = open;
    $[13] = summary;
    $[14] = tone;
    $[15] = T0;
    $[16] = T1;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[15];
    T1 = $[16];
    t10 = $[17];
    t11 = $[18];
    t12 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  if (t12 !== Symbol.for("react.early_return_sentinel")) {
    return t12;
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ChevronRight aria-hidden="true" className="size-3 shrink-0 group-data-[state=open]:rotate-90" />;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== T0 || $[24] !== summary || $[25] !== t8) {
    t14 = <T0 className={t8}>{summary}{t13}</T0>;
    $[23] = T0;
    $[24] = summary;
    $[25] = t8;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== children || $[28] !== detail || $[29] !== tone) {
    t15 = <CollapsibleContent>{detail ? <div className={cn("mt-1 max-h-36 overflow-y-auto whitespace-pre-wrap break-words pr-3 text-base leading-5", tone === "danger" ? "text-red-text" : "text-text-tertiary")}>{detail}</div> : <ol className="mt-1.5 flex flex-col gap-2 border-l border-border-primary pl-4">{children}</ol>}</CollapsibleContent>;
    $[27] = children;
    $[28] = detail;
    $[29] = tone;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== T1 || $[32] !== t10 || $[33] !== t11 || $[34] !== t14 || $[35] !== t15 || $[36] !== t9) {
    t16 = <T1 open={t9} onOpenChange={t10} className={t11}>{t14}{t15}</T1>;
    $[31] = T1;
    $[32] = t10;
    $[33] = t11;
    $[34] = t14;
    $[35] = t15;
    $[36] = t9;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  return t16;
}

/** One step in a ChatActivity timeline: a reasoning note or a tool call. */
export function ChatActivityStep(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <li className="min-w-0 text-base leading-5 text-text-secondary">{children}</li>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function formatDuration(ms: number) {
  if (ms < 1000) return `${Math.max(1, Math.round(ms))}ms`;
  if (ms < 60_000) return `${(Math.floor(ms / 100) / 10).toFixed(1)}s`;
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, "0")}s`;
}
function codeText(value: unknown): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch {
        return value;
      }
    }
    return value;
  }
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}

/** A labelled block of code or JSON with a copy button. */
export function ChatCode(t0) {
  const $ = _c(25);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = codeText(value);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const text = t1;
  const [copied, setCopied] = React.useState(false);
  let t2;
  let t3;
  if ($[2] !== copied) {
    t2 = () => {
      if (!copied) {
        return;
      }
      const timer = window.setTimeout(() => setCopied(false), 1400);
      return () => window.clearTimeout(timer);
    };
    t3 = [copied];
    $[2] = copied;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  React.useEffect(t2, t3);
  let t4;
  if ($[5] !== copied || $[6] !== label) {
    t4 = copied ? "Copied" : `Copy ${label.toLowerCase()}`;
    $[5] = copied;
    $[6] = label;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== text) {
    t5 = () => void navigator.clipboard.writeText(text).then(() => setCopied(true));
    $[8] = text;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== copied) {
    t6 = copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />;
    $[10] = copied;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <IconButton label={t4} size={24} onClick={t5}>{t6}</IconButton>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== label || $[17] !== t7) {
    t8 = <div className="flex items-center justify-between gap-2 text-sm font-medium text-text-tertiary">{label}{t7}</div>;
    $[16] = label;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("max-h-60 overflow-auto whitespace-pre-wrap break-words rounded-control bg-bg-primary p-2.5", "font-mono text-sm leading-5 text-text-secondary");
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== text) {
    t10 = <pre className={t9}><code>{text}</code></pre>;
    $[20] = text;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8) {
    t11 = <div className="flex min-w-0 flex-col gap-1">{t8}{t10}</div>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}
export type ChatToolStatus = "running" | "success" | "error";

/** One tool call as a compact activity row: icon, name, summary and status, opening to its arguments and result. */
export function ChatToolCard(t0) {
  const $ = _c(51);
  const {
    name,
    summary,
    icon,
    status,
    args,
    result,
    error,
    duration,
    onRetry
  } = t0;
  const [open, setOpen] = React.useState(status === "error");
  const t1 = status === "running" ? `${name} running` : status === "success" ? `${name} finished` : `${name} failed${error ? `: ${error}` : ""}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="sr-only" role="status" aria-live="polite">{t1}</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("group flex h-7 w-full min-w-0 items-center gap-2 rounded-control px-1.5 text-left text-base leading-5 outline-none", "hover:bg-fill-hover-subtle focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-accent");
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== icon) {
    t4 = icon ?? <Wrench />;
    $[3] = icon;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <span aria-hidden="true" className="grid size-4 shrink-0 place-items-center text-text-tertiary [&_svg]:size-3.5">{t4}</span>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== name) {
    t6 = <code className="shrink-0 truncate font-mono text-sm text-text-secondary" title={name}>{name}</code>;
    $[7] = name;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== summary) {
    t7 = summary != null && <span className="min-w-0 flex-1 truncate text-text-tertiary">{summary}</span>;
    $[9] = summary;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== status) {
    t8 = status === "running" && <Spinner size="xs" />;
    $[11] = status;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== status) {
    t9 = status === "success" && <Check aria-hidden="true" className="size-3.5 text-green-text" />;
    $[13] = status;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== duration || $[16] !== status) {
    t10 = status === "error" ? <Badge color="danger">Failed</Badge> : duration != null && formatDuration(duration);
    $[15] = duration;
    $[16] = status;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t8 || $[20] !== t9) {
    t11 = <span className="ml-auto flex shrink-0 items-center gap-1.5 text-sm tabular-nums text-text-tertiary">{t8}{t9}{t10}</span>;
    $[18] = t10;
    $[19] = t8;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ChevronRight aria-hidden="true" className="size-3.5 shrink-0 text-text-quaternary group-data-[state=open]:rotate-90" />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t11 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7) {
    t13 = <CollapsibleTrigger className={t3}>{t5}{t6}{t7}{t11}{t12}</CollapsibleTrigger>;
    $[23] = t11;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== error || $[29] !== onRetry || $[30] !== status) {
    t14 = status === "error" && <div role="alert" className="flex items-start gap-2 text-base leading-5 text-red-text"><CircleAlert aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" /><p className="min-w-0 flex-1 whitespace-pre-wrap">{error ?? "The tool call failed."}</p>{onRetry && <Button variant="secondary" size="sm" onClick={onRetry}>Retry</Button>}</div>;
    $[28] = error;
    $[29] = onRetry;
    $[30] = status;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== args) {
    t15 = args !== undefined && <ChatCode label="Arguments" value={args} />;
    $[32] = args;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== result || $[35] !== status) {
    t16 = result !== undefined ? <ChatCode label="Result" value={result} /> : status === "running" && <Text as="p" size="sm" tone="tertiary">Waiting for result…</Text>;
    $[34] = result;
    $[35] = status;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== args || $[38] !== result || $[39] !== status) {
    t17 = args === undefined && result === undefined && status === "success" && <Text as="p" size="sm" tone="tertiary">No arguments or result.</Text>;
    $[37] = args;
    $[38] = result;
    $[39] = status;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t14 || $[42] !== t15 || $[43] !== t16 || $[44] !== t17) {
    t18 = <CollapsibleContent className="ml-3.5 mt-1 flex flex-col gap-3 border-l border-border-primary pb-1 pl-4">{t14}{t15}{t16}{t17}</CollapsibleContent>;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== open || $[47] !== t13 || $[48] !== t18 || $[49] !== t2) {
    t19 = <Collapsible open={open} onOpenChange={setOpen} className="min-w-0">{t2}{t13}{t18}</Collapsible>;
    $[46] = open;
    $[47] = t13;
    $[48] = t18;
    $[49] = t2;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  return t19;
}

/**
 * The chat screen: the conversation list beside the conversation. Below md
 * only one shows, chosen by `view`, so the list is the first screen and a
 * conversation opens full width.
 */
export function ChatLayout(t0) {
  const $ = _c(11);
  const {
    history,
    view: t1,
    children
  } = t0;
  const view = t1 === undefined ? "conversation" : t1;
  let t2;
  if ($[0] !== history || $[1] !== view) {
    t2 = history && <div className={cn("flex min-h-0 w-full shrink-0 md:w-64 md:border-r md:border-border-primary", view === "conversation" && "max-md:hidden")}>{history}</div>;
    $[0] = history;
    $[1] = view;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = Boolean(history) && view === "list" && "max-md:hidden";
  let t4;
  if ($[3] !== t3) {
    t4 = cn("flex min-h-0 min-w-0 flex-1", t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== children || $[6] !== t4) {
    t5 = <div className={t4}>{children}</div>;
    $[5] = children;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t5) {
    t6 = <div className="flex min-h-0 flex-1 overflow-hidden bg-bg-surface">{t2}{t5}</div>;
    $[8] = t2;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}

/**
 * The conversation column: a page header, a scrolling transcript, and the
 * composer docked under it behind a fade. `dropping` covers it while files drag over.
 */
export function ChatMain(t0) {
  const $ = _c(16);
  let children;
  let composer;
  let dropping;
  let header;
  let props;
  if ($[0] !== t0) {
    ({
      header,
      children,
      composer,
      dropping,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = composer;
    $[3] = dropping;
    $[4] = header;
    $[5] = props;
  } else {
    children = $[1];
    composer = $[2];
    dropping = $[3];
    header = $[4];
    props = $[5];
  }
  let t1;
  if ($[6] !== dropping) {
    t1 = dropping && <div role="status" className={cn("pointer-events-none absolute inset-3 z-20 flex flex-col items-center justify-center gap-3", "rounded-popover border border-dashed border-border-tertiary bg-bg-surface text-base text-text-secondary [&_svg]:size-6")}>{dropping}</div>;
    $[6] = dropping;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== composer) {
    t2 = composer && <div className="relative mx-auto w-full max-w-3xl shrink-0 px-4 pb-4 sm:px-6 sm:pb-5"><div aria-hidden="true" className="pointer-events-none absolute inset-x-0 -top-6 h-6 bg-gradient-to-t from-bg-surface" /><div className="relative flex flex-col gap-2">{composer}</div></div>;
    $[8] = composer;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== children || $[11] !== header || $[12] !== props || $[13] !== t1 || $[14] !== t2) {
    t3 = <section className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-bg-surface" {...props}>{header}{t1}{children}{t2}</section>;
    $[10] = children;
    $[11] = header;
    $[12] = props;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}

/** The transcript's scroll container and its centred reading column. */
export function ChatTranscript(t0) {
  const $ = _c(19);
  let children;
  let contentRef;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      centered: t1,
      contentRef,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = contentRef;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
  } else {
    children = $[1];
    contentRef = $[2];
    props = $[3];
    ref = $[4];
    t1 = $[5];
  }
  const centered = t1 === undefined ? false : t1;
  const t2 = centered && "flex items-center";
  let t3;
  if ($[6] !== t2) {
    t3 = cn("min-h-0 flex-1 overflow-y-auto overscroll-contain outline-none", "focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-accent", t2);
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = centered ? "py-6 sm:pb-24" : "pb-8 pt-6 sm:pt-8";
  let t5;
  if ($[8] !== t4) {
    t5 = cn("mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 sm:px-6", t4);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== children || $[11] !== contentRef || $[12] !== t5) {
    t6 = <div ref={contentRef} className={t5}>{children}</div>;
    $[10] = children;
    $[11] = contentRef;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== props || $[15] !== ref || $[16] !== t3 || $[17] !== t6) {
    t7 = <div ref={ref} tabIndex={0} className={t3} {...props}>{t6}</div>;
    $[14] = props;
    $[15] = ref;
    $[16] = t3;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}

/** Jumps back to the newest message; sits above the composer and shows while the reader has scrolled up. */
export function ChatScrollButton(t0) {
  const $ = _c(3);
  const {
    visible,
    onClick
  } = t0;
  if (!visible) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ArrowDown aria-hidden="true" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onClick) {
    t2 = <div className="absolute -top-10 left-1/2 z-10 -translate-x-1/2"><IconButton label="Scroll to latest message" variant="secondary" onClick={onClick}>{t1}</IconButton></div>;
    $[1] = onClick;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

/** The conversation list: a new-conversation button over conversations grouped by recency. */
export function ChatHistory(t0) {
  const $ = _c(17);
  const {
    children,
    onNew,
    newDisabled,
    loading: t1,
    empty,
    footer
  } = t0;
  const loading = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SquarePen aria-hidden="true" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== newDisabled || $[2] !== onNew) {
    t3 = <div className="flex h-11 shrink-0 items-center px-2"><Button variant="ghost" disabled={newDisabled} onClick={onNew}>{t2}New conversation</Button></div>;
    $[1] = newDisabled;
    $[2] = onNew;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = loading || undefined;
  let t5;
  if ($[4] !== loading) {
    t5 = loading && <div role="status" className="flex flex-col gap-4 px-2 py-2.5"><span className="sr-only">Loading history…</span>{["w-3/4", "w-11/12", "w-2/3", "w-4/5", "w-1/2"].map(_temp2)}</div>;
    $[4] = loading;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== empty) {
    t6 = empty && <div className="px-2 py-1.5"><Text as="p" size="sm" tone="tertiary">{empty}</Text></div>;
    $[6] = empty;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== children || $[9] !== footer || $[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t7 = <nav aria-label="Conversations" aria-busy={t4} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain px-2 pb-3">{t5}{t6}{children}{footer}</nav>;
    $[8] = children;
    $[9] = footer;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t3 || $[15] !== t7) {
    t8 = <aside aria-label="Conversation history" className="flex min-h-0 w-full flex-1 flex-col bg-bg-primary">{t3}{t7}</aside>;
    $[14] = t3;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}

/** A recency group in the conversation list (Today, Yesterday, Previous 7 days, Older). */
function _temp2(width) {
  return <Skeleton key={width} size="xs" className={width} />;
}
export function ChatHistoryGroup(t0) {
  const $ = _c(5);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ul className="flex flex-col gap-px">{children}</ul>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== t1) {
    t2 = <ListItemGroup label={label}>{t1}</ListItemGroup>;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/**
 * A conversation in the list: a 32px row with its title and, on the right,
 * its date, which gives way to the action menu on hover.
 */
export function ChatHistoryItem(t0) {
  const $ = _c(25);
  let actions;
  let meta;
  let props;
  let t1;
  let title;
  if ($[0] !== t0) {
    ({
      title,
      meta,
      actions,
      selected: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = actions;
    $[2] = meta;
    $[3] = props;
    $[4] = t1;
    $[5] = title;
  } else {
    actions = $[1];
    meta = $[2];
    props = $[3];
    t1 = $[4];
    title = $[5];
  }
  const selected = t1 === undefined ? false : t1;
  const t2 = selected ? "page" : undefined;
  const t3 = selected ? "bg-fill-sidebar-active text-text-primary" : "text-text-secondary";
  let t4;
  if ($[6] !== t3) {
    t4 = cn("flex h-8 w-full min-w-0 items-center gap-2 rounded-control px-2 text-left text-base leading-5 outline-none", "hover:bg-fill-sidebar-hover focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-accent", "disabled:cursor-default disabled:opacity-disabled", t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== title) {
    t5 = <span className="min-w-0 flex-1 truncate">{title}</span>;
    $[8] = title;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== actions || $[11] !== meta) {
    t6 = meta && <span className={cn("shrink-0 text-sm tabular-nums text-text-tertiary", Boolean(actions) && "group-focus-within:invisible group-hover:invisible group-has-[[data-state=open]]:invisible")}>{meta}</span>;
    $[10] = actions;
    $[11] = meta;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== props || $[14] !== t2 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== title) {
    t7 = <button type="button" aria-current={t2} title={title} className={t4} {...props}>{t5}{t6}</button>;
    $[13] = props;
    $[14] = t2;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = title;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== actions) {
    t8 = actions && <span className="absolute right-1 top-1/2 flex -translate-y-1/2">{actions}</span>;
    $[20] = actions;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = <li className="group relative">{t7}{t8}</li>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}

/** The hover action menu on a ChatHistoryItem; children are DropdownMenuItems. */
export function ChatHistoryActions(t0) {
  const $ = _c(9);
  const {
    label,
    disabled,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <MoreHorizontal aria-hidden="true" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== disabled || $[2] !== label) {
    t2 = <DropdownMenuTrigger asChild={true}><IconButton label={label} size={24} reveal={true} showTooltip={false} disabled={disabled}>{t1}</IconButton></DropdownMenuTrigger>;
    $[1] = disabled;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children) {
    t3 = <DropdownMenuContent align="end">{children}</DropdownMenuContent>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <DropdownMenu>{t2}{t3}</DropdownMenu>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

/** The blank conversation: an empty state greeting, the composer and starter prompts. */
export function ChatWelcome(t0) {
  const $ = _c(11);
  const {
    icon,
    title,
    composer,
    children
  } = t0;
  let t1;
  if ($[0] !== icon || $[1] !== title) {
    t1 = <EmptyState icon={icon} title={title} />;
    $[0] = icon;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== composer) {
    t2 = <div className="w-full">{composer}</div>;
    $[3] = composer;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children) {
    t3 = children && <ul aria-label="Suggestions" className="flex flex-wrap justify-center gap-2">{children}</ul>;
    $[5] = children;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <section aria-label="New conversation" className="flex w-full flex-col items-center gap-4">{t1}{t2}{t3}</section>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}

/** A starter prompt chip under the welcome composer. */
export function ChatSuggestion(t0) {
  const $ = _c(8);
  const {
    icon,
    title,
    prompt,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== onSelect || $[1] !== prompt) {
    t1 = () => onSelect(prompt);
    $[0] = onSelect;
    $[1] = prompt;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== icon || $[4] !== prompt || $[5] !== t1 || $[6] !== title) {
    t2 = <li><Button variant="secondary" title={prompt} onClick={t1}>{icon}{title}</Button></li>;
    $[3] = icon;
    $[4] = prompt;
    $[5] = t1;
    $[6] = title;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/** Placeholder turns while a conversation loads. */
export function ChatTranscriptSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div role="status" className="flex flex-col gap-9"><span className="sr-only">Loading conversation…</span>{[["w-1/2", "w-11/12", "w-4/5", "w-2/5"], ["w-1/3", "w-full", "w-5/6", "w-2/3"]].map(_temp4)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp4(lines, turn) {
  return <div key={turn} className="flex flex-col gap-2.5"><Skeleton size="xl" shape="pill" className={cn("mb-3 self-end", lines[0])} />{lines.slice(1).map(_temp3)}</div>;
}
function _temp3(width) {
  return <Skeleton key={width} size="xs" className={width} />;
}